// Form fields configuration with labels, placeholders, icons, types, and names

import * as Yup from "yup";

export const stepsNames = [
  "Confirm Job Information",
  "Select the Pricing Model For this Job",
  "Upload Supporting Media",
  "Confirm Job Details",
];

export const Title = {
  0: {
    title: "Ready to Get Paid?",
    subTitle: "Let’s confirm some information first. ",
  },
  1: {
    title: "Materials & Labor Pricing",
    subTitle: "",
  },
  2: {
    title: "Please upload any supporting media for this job",
  },
  3: {
    title: "Please confirm all details are complete and accurate.",
    subTitle: "",
  },
};

export const formFields = {
  step1: {
    job_number: {
      label: "Job Number",
      placeholder: "Enter job number",
      startIcon: "",
      type: "text",
      name: "job_number",
    },
    date_completed: {
      label: "Date of Completion",
      placeholder: "Enter date of completion",
      startIcon: "",
      type: "date",
      name: "dateOfCompletion",
    },
    property_address: {
      label: "Property Address",
      placeholder: "Enter property address",
      startIcon: "",
      type: "text",
      name: "property_address",
    },
    property_city: {
      label: "City",
      placeholder: "Enter city",
      startIcon: "",
      type: "text",
      name: "property_city",
    },
    property_state: {
      label: "State",
      placeholder: "Enter state",
      startIcon: "",
      type: "text",
      name: "property_state",
    },
    property_zip: {
      label: "ZIP",
      placeholder: "Enter ZIP code",
      startIcon: "",
      type: "text",
      name: "property_zip",
    },
  },
  step2: {
    material_cost: {
      label: "Material Cost",
      placeholder: "Enter dollar amount",
      startIcon: "",
      type: "number",
      name: "material_cost",
    },
    labor_cost: {
      label: "Labor Cost",
      placeholder: "Enter dollar amount",
      startIcon: "",
      type: "number",
      name: "labor_cost",
    },
    job_cost: {
      label: "Job Cost",
      placeholder: "Enter dollar amount",
      startIcon: "",
      type: "number",
      name: "job_cost",
    },
    line_items: {
      label: "Additional Cost Incurred",
      placeholder: "$500.00",
      startIcon: "",
      type: "number",
      name: "line_items",
    },
    additionalItem: {
      label: "Item",
      placeholder: "Trailer Rental",
      startIcon: "",
      type: "text",
      name: "additionalItem",
    },
  },
  step3: {
    jobMedia: {
      label: "Add a Photo or Video :",
      placeholder:
        "Any additional media will help us assess your issue faster.",
      startIcon: "🏠",
      type: "file",
      name: "jobMedia",
      accept: "image/*,video/*",
    },
  },
};

// Validation schemas for each step
export const validationSchemas = {
  0: Yup.object().shape({
    job_number: Yup.string().required("Please enter your job number"),
    date_completed: Yup.date()
      .required("Please enter date of completion")
      .typeError("Date of completion must be a valid date"),
    property_address: Yup.string().required("Please enter property address"),
    property_city: Yup.string().required("Please enter city"),
    property_state: Yup.string().required("Please enter state"),
    property_zip: Yup.string()
      .required("Please enter ZIP code")
      .matches(/^\d{5}$/, "ZIP code must be exactly 5 digits"),
  }),

  1: Yup.object()
    .shape({
      material_cost: Yup.number()
        .nullable()
        .min(0, "Must be a positive number"),
      labor_cost: Yup.number().nullable().min(0, "Must be a positive number"),
      job_cost: Yup.number().nullable().min(0, "Must be a positive number"),
      line_items: Yup.array()
        .of(
          Yup.object().shape({
            amount: Yup.number()
              .required("Amount is required")
              .min(0, "Must be a positive number"),
            description: Yup.string().required("Item is required"),
          })
        )
        .nullable()
        .notRequired(),
    })
    .test(
      "cost-validation",
      "Either provide both material and labor costs or a job cost, but not both",
      function (values) {
        const { material_cost, labor_cost, job_cost } = values;

        // Treat 0 as equivalent to null/undefined
        const hasItemizedCosts =
          material_cost != null &&
          material_cost !== 0 &&
          labor_cost != null &&
          labor_cost !== 0;
        const hasJobCost = job_cost != null && job_cost !== 0;

        if (!hasItemizedCosts && !hasJobCost) {
          return this.createError({
            path: "material_cost",
            message: "Either material and labor costs or job cost is required",
          });
        }
        if (hasItemizedCosts && hasJobCost) {
          return this.createError({
            path: "job_cost",
            message: "Cannot provide both itemized costs and job cost",
          });
        }
        if (
          material_cost != null &&
          material_cost !== 0 &&
          (labor_cost == null || labor_cost === 0)
        ) {
          return this.createError({
            path: "labor_cost",
            message: "Labor cost is required when material cost is provided",
          });
        }
        if (
          labor_cost != null &&
          labor_cost !== 0 &&
          (material_cost == null || material_cost === 0)
        ) {
          return this.createError({
            path: "material_cost",
            message: "Material cost is required when labor cost is provided",
          });
        }

        return true;
      }
    ),

  2: Yup.object().shape({
    jobMedia: Yup.mixed()
      .nullable()
      .test("fileSize", "File size must be less than 5MB", (value) => {
        if (!value) return true; // Optional field
        return value.size <= 5 * 1024 * 1024; // 5MB limit
      })
      .test("fileType", "Only image and video files are allowed", (value) => {
        if (!value) return true; // Optional field
        return [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "video/mp4",
          "video/quicktime", // for .mov files
          "video/x-msvideo", // for .avi files
        ].includes(value.type);
      }),
  }),
};

export const stepFieldsMap = {
  0: [
    "job_number",
    "date_completed",
    "property_address",
    "property_city",
    "property_state",
    "property_zip",
  ],
  1: ["material_cost", "labor_cost", "job_cost", "line_items"],
  2: ["jobMedia"],
};
